import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { Avatar, Box, Group, Loader, Text, Title, UnstyledButton } from '@mantine/core';
import { clientSupportChatApi } from '@modules/client-support-chat/api/client-support-chat.api';
import {
  appendClientSupportMessageToCache,
  CHAT_THREADS_QUERY_KEY,
  CHAT_UNREAD_QUERY_KEY,
  getActiveAdminChatUserId,
  setActiveAdminChatUserId,
} from '@modules/client-support-chat/chat-cache';
import type {
  ClientSupportThreadSummary,
  ClientSupportThreadsResponse,
} from '@modules/client-support-chat/types';
import { useClientSupportChatSocket } from '@modules/client-support-chat/hooks/useClientSupportChatSocket';
import { ClientSupportChatPanel } from '@modules/client-support-chat/components/ClientSupportChatPanel';
import { Input } from '@shared/ui';
import { useDebounce } from '@shared/hooks';
import '@shared/styles/page.css';
import '@modules/client-support-chat/components/ClientSupportChatPanel.css';

function pad2(n: number) {
  return String(n).padStart(2, '0');
}

/** В списке диалогов: время, если сообщение сегодня, иначе дата. */
function threadListStamp(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const now = new Date();
  const sameDay =
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate();
  if (sameDay) return `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
  const sameYear = d.getFullYear() === now.getFullYear();
  return sameYear
    ? `${pad2(d.getDate())}.${pad2(d.getMonth() + 1)}`
    : `${pad2(d.getDate())}.${pad2(d.getMonth() + 1)}.${d.getFullYear()}`;
}

function userInitials(name: string) {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 1).toUpperCase();
  return `${parts[0].slice(0, 1)}${parts[1].slice(0, 1)}`.toUpperCase();
}

function displayName(name: string | null | undefined, userId: number) {
  return name?.trim() || `Клиент #${userId}`;
}

function upsertThreadInCache(
  prev: ClientSupportThreadsResponse | undefined,
  thread: ClientSupportThreadSummary,
): ClientSupportThreadsResponse {
  const items = prev?.items ?? [];
  const without = items.filter((item) => item.userId !== thread.userId);
  return {
    items: [thread, ...without],
    total: without.length === items.length ? (prev?.total ?? 0) + 1 : (prev?.total ?? items.length),
  };
}

export default function ClientSupportChatPage() {
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const userIdFromQuery = Number(searchParams.get('userId'));
  const userNameFromQuery = searchParams.get('userName')?.trim() || null;
  const [selectedUserId, setSelectedUserId] = useState<number | null>(
    Number.isFinite(userIdFromQuery) && userIdFromQuery > 0 ? userIdFromQuery : null,
  );
  const [selectedUserName, setSelectedUserName] = useState<string | null>(userNameFromQuery);
  const [selectedUserPhone, setSelectedUserPhone] = useState<string | null>(null);
  const [userSearch, setUserSearch] = useState('');
  const debouncedUserSearch = useDebounce(userSearch, 300);
  const isSearching = debouncedUserSearch.trim().length > 0;
  const ensuredUserIdsRef = useRef<Set<number>>(new Set());

  const threadsQuery = useQuery({
    queryKey: CHAT_THREADS_QUERY_KEY,
    queryFn: () => clientSupportChatApi.listThreads({ limit: 100 }),
    staleTime: 0,
    refetchOnMount: 'always',
  });

  const usersSearchQuery = useQuery({
    queryKey: ['clients', 'support-chat-search', debouncedUserSearch],
    queryFn: () => clientSupportChatApi.searchUsers(debouncedUserSearch),
    enabled: isSearching,
  });

  const threads = threadsQuery.data?.items ?? [];
  const activeUserId = selectedUserId;
  const activeThread = useMemo((): ClientSupportThreadSummary | null =>
    threads.find((thread) => thread.userId === activeUserId) ?? null,
    [activeUserId, threads],
  );

  const ensureThreadMutation = useMutation({
    mutationFn: (userId: number) => clientSupportChatApi.ensureThread(userId),
    onSuccess: (thread) => {
      setSelectedUserName(thread.userName);
      setSelectedUserPhone(thread.userPhone);
      queryClient.setQueryData<ClientSupportThreadsResponse>(CHAT_THREADS_QUERY_KEY, (prev) =>
        upsertThreadInCache(prev, thread),
      );
    },
  });

  useEffect(() => {
    if (activeUserId == null || activeUserId <= 0) return;
    if (activeThread) {
      ensuredUserIdsRef.current.add(activeUserId);
      return;
    }
    if (!threadsQuery.isSuccess) return;
    if (ensuredUserIdsRef.current.has(activeUserId)) return;
    ensuredUserIdsRef.current.add(activeUserId);
    ensureThreadMutation.mutate(activeUserId);
  }, [activeUserId, activeThread, threadsQuery.isSuccess, ensureThreadMutation.mutate]);

  const activeUserName =
    activeThread?.userName ??
    selectedUserName ??
    (activeUserId ? `Клиент #${activeUserId}` : null);
  const activeUserPhone = activeThread?.userPhone ?? selectedUserPhone;

  const openClientChat = useCallback((userId: number, userName: string | null, userPhone?: string | null) => {
    setSelectedUserId(userId);
    setSelectedUserName(userName);
    setSelectedUserPhone(userPhone ?? null);
    setUserSearch('');
    setSearchParams({ userId: String(userId) }, { replace: true });
  }, [setSearchParams]);

  useEffect(() => {
    if (!Number.isFinite(userIdFromQuery) || userIdFromQuery <= 0) return;
    setSelectedUserId(userIdFromQuery);
    if (userNameFromQuery) {
      setSelectedUserName(userNameFromQuery);
    }
  }, [userIdFromQuery, userNameFromQuery]);

  useEffect(() => {
    setActiveAdminChatUserId(activeUserId);
    return () => setActiveAdminChatUserId(null);
  }, [activeUserId]);

  useClientSupportChatSocket({
    onMessage: ({ userId, userName, userPhone, message }) => {
      appendClientSupportMessageToCache(queryClient, userId, message, {
        userName,
        userPhone,
      });
    },
    onUnread: ({ scope, count, userId }) => {
      if (scope !== 'admin') return;
      queryClient.invalidateQueries({ queryKey: CHAT_THREADS_QUERY_KEY });
      if (userId != null && getActiveAdminChatUserId() === userId) return;
      queryClient.setQueryData(CHAT_UNREAD_QUERY_KEY, { count });
    },
  });

  const userSearchResults = usersSearchQuery.data ?? [];

  return (
    <div className="page-container client-support-chat-page">
      <Group className="client-support-chat-page__header">
        <Title order={2}>Чаты с клиентами</Title>
      </Group>

      <div className="client-support-inbox">
        <div className="client-support-inbox__threads">
          <div className="client-support-inbox__threads-head">
            <Input
              placeholder="Найти клиента по имени или телефону…"
              value={userSearch}
              onChange={(event) => setUserSearch(event.currentTarget.value)}
            />
          </div>

          <div className="client-support-inbox__threads-list">
            {isSearching ? (
              <>
                <Text className="client-support-inbox__section-label">Клиенты</Text>
                {usersSearchQuery.isLoading ? (
                  <Group justify="center" py="lg">
                    <Loader size="sm" />
                  </Group>
                ) : null}
                {!usersSearchQuery.isLoading && userSearchResults.length === 0 ? (
                  <Text size="sm" c="dimmed" className="client-support-inbox__empty">
                    Клиенты не найдены
                  </Text>
                ) : null}
                {userSearchResults.map((user) => {
                  const active = user.userId === activeUserId;
                  const title = displayName(user.name, user.userId);
                  return (
                    <UnstyledButton
                      key={user.userId}
                      className={`client-support-inbox__seller-result ${active ? 'client-support-inbox__seller-result--active' : ''}`}
                      onClick={() => openClientChat(user.userId, user.name, user.phone)}
                    >
                      <Group wrap="nowrap" gap="sm" align="flex-start">
                        <Avatar radius={8} size={36} color="gray">
                          {userInitials(title)}
                        </Avatar>
                        <div className="client-support-inbox__thread-body">
                          <Text fw={600} size="sm" lineClamp={1}>
                            {title}
                          </Text>
                          <Text size="xs" c="dimmed" mt={2}>
                            {user.phone?.trim() || 'Написать клиенту'}
                          </Text>
                        </div>
                      </Group>
                    </UnstyledButton>
                  );
                })}
              </>
            ) : (
              <>
                <Text className="client-support-inbox__section-label">Диалоги</Text>
                {threadsQuery.isLoading ? (
                  <Group justify="center" py="xl">
                    <Loader size="sm" />
                  </Group>
                ) : null}
                {!threadsQuery.isLoading && threads.length === 0 ? (
                  <Text size="sm" c="dimmed" className="client-support-inbox__empty">
                    Пока нет чатов
                  </Text>
                ) : null}
                {threads.map((thread) => {
                  const active = thread.userId === activeUserId;
                  const title = displayName(thread.userName, thread.userId);
                  return (
                    <UnstyledButton
                      key={thread.threadId}
                      className={`client-support-inbox__thread ${active ? 'client-support-inbox__thread--active' : ''}`}
                      onClick={() => openClientChat(thread.userId, thread.userName, thread.userPhone)}
                    >
                      <Group wrap="nowrap" gap="sm" align="flex-start">
                        <Avatar radius={8} size={36} color="gray">
                          {userInitials(title)}
                        </Avatar>
                        <div className="client-support-inbox__thread-body">
                          <div className="client-support-inbox__thread-top">
                            <span className="client-support-inbox__thread-title">{title}</span>
                            <span className="client-support-inbox__thread-top-right">
                              {thread.hasOpenOrderProblem ? (
                                <span
                                  className="client-support-inbox__problem-mark"
                                  title="Есть незакрытая проблема по заказу"
                                >
                                  !
                                </span>
                              ) : null}
                              {thread.lastMessage ? (
                                <time
                                  className="client-support-inbox__thread-stamp"
                                  dateTime={thread.lastMessage.createdAt}
                                >
                                  {threadListStamp(thread.lastMessage.createdAt)}
                                </time>
                              ) : null}
                            </span>
                          </div>
                          <div className="client-support-inbox__thread-preview-row">
                            <span className="client-support-inbox__thread-preview">
                              {thread.lastMessage?.body?.trim()
                                || (thread.lastMessage?.imageUrls?.length
                                  ? (thread.lastMessage.imageUrls.length === 1
                                    ? 'Фото'
                                    : `Фото (${thread.lastMessage.imageUrls.length})`)
                                  : 'Написать клиенту')}
                            </span>
                            {thread.unreadCount > 0 ? (
                              <span className="client-support-inbox__unread-count">
                                {thread.unreadCount > 99 ? '99+' : thread.unreadCount}
                              </span>
                            ) : null}
                          </div>
                        </div>
                      </Group>
                    </UnstyledButton>
                  );
                })}
              </>
            )}
          </div>
        </div>

        <Box className="client-support-inbox__chat">
          {activeUserId ? (
            <ClientSupportChatPanel
              userId={activeUserId}
              userName={activeUserName}
              userPhone={activeUserPhone}
            />
          ) : (
            <div className="client-support-inbox__chat-empty">
              <p className="client-support-inbox__chat-empty-text">
                Найдите клиента или выберите диалог слева
              </p>
            </div>
          )}
        </Box>
      </div>
    </div>
  );
}
